import { all, call, fork, put, select, takeEvery } from 'redux-saga/effects';
import { errorRequest, tokenRequest } from '../../../services/product';
import { IToken, TokenState, restartToken, updateToken, updateTokenState } from 'core/slices/session';
import { PayloadAction } from '@reduxjs/toolkit';
import { ITokenAction } from 'types/action/player';
import { RootState } from 'types';
import { selectToken } from 'core/selectors/product/token';
import { JWT_TOKEN, REFRESH_TOKEN } from '../../../../constants';
import { handlePlayerResponse } from './response';
import { IUpdateState, restoreState, updateState } from 'core/actions/product/state';
import { EntityTypes } from 'constants/entities';
import { decrementIndex, incrementIndex, setIndex } from 'core/slices/register';
import { resetIndex } from 'core/slices/register';
import { selectRegister } from 'core/selectors/product/register';
import { updatePlayerState } from 'core/slices/player';
import { removeVideo } from 'core/sagas/player/video';
import { IActionFeed, IActionFeedItem, selectActionFeed } from 'core/selectors/product/action/actionFeed';
import { systemSigninRequest } from 'core/services/authentication';
import { handleAuthResponse } from 'core/sagas/auth/auth';
import { LoadingState } from 'types/domain/base';
import { applyTheme } from 'utils/applyTheme';
import { IState } from 'react-use/lib/usePermission';
import packageJson from '../../../../../package.json';

function* tokenAction(action) {
  const { playerResponse, theme } = action.payload;
  const aId = yield select((state: RootState) => state.state[EntityTypes.COURSE_PROGRESS]);

  if (!playerResponse) return null;

  if (theme) applyTheme(theme.config);

  if (playerResponse.courseProgressId && aId !== playerResponse.courseProgressId)
    yield put(yield call(updateState, { type: EntityTypes.COURSE_PROGRESS, id: playerResponse.courseProgressId }));

  yield handleAuthResponse(action.payload);

  if (!playerResponse.queue?.length) {
    yield put(yield call(errorRequest, { code: 0, message: 'Empty queue', stackTrace: 'tokenRequest.fullfilled' }));
    yield put(
      yield call(updateTokenState, {
        token: action.payload.token,
        loading: LoadingState.ERROR,
        message: 'Token is empty',
      }),
    );
  } else {
    const token = yield select((state: RootState) => state.state[EntityTypes.TOKEN]);

    if (action.meta.arg.inviteToken && playerResponse.token && playerResponse.token !== token) {
      yield put(
        yield call(updateToken, {
          token: playerResponse.token,
          inviteToken: action.meta.arg.inviteToken,
          courseProgressId: playerResponse.courseProgressId,
          state: TokenState.NEW,
          renew: true,
        } as IToken),
      );
    }
    yield handlePlayerResponse(playerResponse);
  }
}

function* updateTokenSaga(action: PayloadAction<IToken>) {
  const session = yield select((state: RootState) => state.session);

  //@ts-ignore
  const token: IToken = yield select(state => selectToken(state, action.payload.token));
  const state: IState = yield select((state: RootState) => state.state);

  const jwt = yield call([localStorage, localStorage.getItem], JWT_TOKEN);
  const refresh = yield call([localStorage, localStorage.getItem], REFRESH_TOKEN);
  const isNewUser = action.payload.inviteToken != null;
  const isStart = isNewUser || token?.state !== TokenState.CAN_RESTART;
  let isEmpty = false;

  if (token?.courseProgressId) {
    if (
      token.courseProgressId !== state[EntityTypes.COURSE_PROGRESS] ||
      state[EntityTypes.ACTION] !== token.actionId ||
      state[EntityTypes.MODULE] !== token.moduleId ||
      state[EntityTypes.SESSION_ATTEMPT] !== token.sessionAttemptId
    ) {
      yield put(
        yield call(restoreState, [
          { type: EntityTypes.COURSE_PROGRESS, id: token.courseProgressId },
          { type: EntityTypes.SESSION_ATTEMPT, id: token.sessionAttemptId },
          { type: EntityTypes.MODULE, id: token.moduleId },
          { type: EntityTypes.ACTION, id: token.actionId },
        ]),
      );
    }
    const actionFeed: IActionFeed = yield select(selectActionFeed);
    const index =
      token?.index && actionFeed.feed[token?.index]
        ? token.index
        : (() => {
            const ri = [...actionFeed.feed]
              .reverse()
              .findIndex((item: IActionFeedItem) => item.actionId === token.actionId);
            return ri === -1 ? -1 : actionFeed.feed.length - 1 - ri;
          })();

    if (index > -1) yield put(yield call(setIndex, index));
    else if (!actionFeed.feed.length) isEmpty = true;
  } else {
    // if (state[EntityTypes.COURSE_PROGRESS])
    //   yield put(yield call(updateState, { type: EntityTypes.COURSE_PROGRESS, id: null }));
    if (!isStart) yield put(yield call(resetIndex));
  }

  if ((isStart || isEmpty) && !action.payload.scorm && !action.payload.renew)
    yield all([
      put(
        yield call(tokenRequest, {
          ...action.payload,
          jwt: jwt,
          refresh: refresh,
          sessionId: session.session?.id,
          tokenCreated: token?.created || new Date().getTime(),
          version: packageJson.version,
        } as ITokenAction),
      ),
    ]);
}

function* updateStateSaga(action: PayloadAction<IUpdateState>) {
  const token: IToken = yield select(selectToken);
  if (token)
    switch (action.payload.type) {
      case EntityTypes.MODULE:
        yield put(yield call(updateTokenState, { token: token.token, moduleId: action.payload.id } as IToken));
        break;
      case EntityTypes.ACTION:
        yield put(yield call(updateTokenState, { token: token.token, actionId: action.payload.id } as IToken));
        break;
      case EntityTypes.SESSION_ATTEMPT:
        yield put(yield call(updateTokenState, { token: token.token, sessionAttemptId: action.payload.id } as IToken));
        break;
    }
}

function* updateIndexSaga(action) {
  const { index } = yield select(selectRegister);
  const token = yield select(selectToken);
  if (token) yield put(yield call(updateTokenState, { token: token.token, index } as IToken));
}

function* restartTokenSaga() {
  const jwt = yield call([localStorage, localStorage.getItem], JWT_TOKEN);
  const refresh = yield call([localStorage, localStorage.getItem], REFRESH_TOKEN);
  const session = yield select((state: RootState) => state.session);
  const token: IToken = yield select(selectToken);

  yield all([
    put(
      yield call(restoreState, [
        { type: EntityTypes.COURSE_PROGRESS, id: null },
        { type: EntityTypes.SESSION_ATTEMPT, id: null },
        { type: EntityTypes.MODULE, id: null },
        { type: EntityTypes.ACTION, id: null },
        { type: EntityTypes.SCENE, id: null },
        { type: EntityTypes.VERIFICATION_REQUEST, id: null },
      ]),
    ),
    put(yield call(updatePlayerState, { paused: true, culprit: 'restartTokenSaga' })),
    put(yield call(resetIndex)),
    removeVideo(),

    put(
      yield call(tokenRequest, {
        token: token.token,
        inviteToken: token.inviteToken,
        jwt: jwt,
        refresh: refresh,
        sessionId: session.session?.id,
        restart: true,
        version: packageJson.version,
      }),
    ),
  ]);
}

function* watchUpdateIndex() {
  yield takeEvery([incrementIndex, decrementIndex], updateIndexSaga);
}

function* watchTokenUpdateAction() {
  yield takeEvery(updateToken.type, updateTokenSaga);
}

function* watchTokenFullfilledAction() {
  yield takeEvery([tokenRequest.fulfilled, systemSigninRequest.fulfilled], tokenAction);
}

function* updateStateAction() {
  yield takeEvery(updateState, updateStateSaga);
}

function* watchRestartToken() {
  yield takeEvery(restartToken, restartTokenSaga);
}

export function* tokenSaga() {
  yield all([
    fork(watchTokenFullfilledAction),
    fork(watchTokenUpdateAction),
    fork(updateStateAction),
    fork(watchRestartToken),
    fork(watchUpdateIndex),
  ]);
}
